import { useState, useEffect, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { InteractionStatus, SilentRequest } from '@azure/msal-browser';
import { loginRequest, apiScopes } from './authConfig';

export interface UserInfo {
  displayName?: string;
  mail?: string;
  userPrincipalName?: string;
  id?: string;
  jobTitle?: string;
  mobilePhone?: string;
  officeLocation?: string;
  preferredLanguage?: string;
  surname?: string;
  givenName?: string;
}

export const useAuth = () => {
  const { instance, accounts, inProgress } = useMsal();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [accessToken, setAccessToken] = useState<string | null>(null);

  // Verifica se l'utente è autenticato
  useEffect(() => {
    const account = accounts[0];
    if (account) {
      setIsAuthenticated(true);
      setUserInfo({
        displayName: account.name || '',
        mail: account.username || '',
        userPrincipalName: account.username || '',
        id: account.localAccountId || ''
      });
    } else {
      setIsAuthenticated(false);
      setUserInfo(null);
    }
    setIsLoading(false);
  }, [accounts]);

  // Logout
  const logout = useCallback(async () => {
    try {
      await instance.logoutPopup({
        postLogoutRedirectUri: "/",
        mainWindowRedirectUri: "/"
      });
      setAccessToken(null);
      setUserInfo(null);
      setIsAuthenticated(false);
    } catch (error) {
      console.error('Logout failed:', error);
      // Fallback: forza il logout locale
      instance.logoutRedirect({
        postLogoutRedirectUri: "/"
      });
    }
  }, [instance]);

  // Ottieni il token di accesso silenziosamente
  const getAccessToken = useCallback(async (): Promise<string | null> => {
    if (!accounts[0]) {
      return null;
    }

    const silentRequest: SilentRequest = {
      ...apiScopes,
      account: accounts[0]
    };

    try {
      const response = await instance.acquireTokenSilent(silentRequest);
      setAccessToken(response.accessToken);
      return response.accessToken;
    } catch (error) {
      console.error('Silent token acquisition failed:', error);
      
      // Se il token non può essere rinnovato silenziosamente, forza il login
      try {
        const response = await instance.acquireTokenPopup(loginRequest);
        setAccessToken(response.accessToken);
        return response.accessToken;
      } catch (interactiveError) {
        console.error('Interactive token acquisition failed:', interactiveError);
        await logout();
        return null;
      }
    }
  }, [accounts, instance, logout]);

  // Login con popup
  const login = useCallback(async () => {
    if (inProgress === InteractionStatus.None) {
      try {
        const response = await instance.loginPopup(loginRequest);
        setAccessToken(response.accessToken);
        return response;
      } catch (error) {
        console.error('Login failed:', error);
        throw error;
      }
    }
  }, [instance, inProgress]);

  // Login con redirect
  const loginRedirect = useCallback(async () => {
    if (inProgress === InteractionStatus.None) {
      try {
        await instance.loginRedirect(loginRequest);
      } catch (error) {
        console.error('Login redirect failed:', error);
        throw error;
      }
    }
  }, [instance, inProgress]);

  // Ottieni informazioni utente da Microsoft Graph
  // const getUserInfo = useCallback(async (): Promise<UserInfo | null> => {
  //   const token = await getAccessToken();
  //   if (!token) {
  //     return null;
  //   }

  //   try {
  //     const response = await fetch('https://graph.microsoft.com/v1.0/me', {
  //       headers: {
  //         'Authorization': `Bearer ${token}`,
  //         'Content-Type': 'application/json'
  //       }
  //     });

  //     if (response.ok) {
  //       const userData = await response.json();
  //       setUserInfo(userData);
  //       return userData;
  //     } else {
  //       console.error('Failed to fetch user info:', response.statusText);
  //       return null;
  //     }
  //   } catch (error) {
  //     console.error('Error fetching user info:', error);
  //     return null;
  //   }
  // }, [getAccessToken]);

  // Verifica e rinnova il token automaticamente
  useEffect(() => {
    if (isAuthenticated && !accessToken) {
      getAccessToken();
    }
  }, [isAuthenticated, accessToken, getAccessToken]);

  // Auto-refresh token ogni 30 minuti
  useEffect(() => {
    if (isAuthenticated) {
      const interval = setInterval(() => {
        getAccessToken();
      }, 30 * 60 * 1000); // 30 minuti

      return () => clearInterval(interval);
    }
  }, [isAuthenticated, getAccessToken]);

  return {
    isAuthenticated,
    isLoading,
    userInfo,
    accessToken,
    login,
    loginRedirect,
    logout,
    getAccessToken,
    //getUserInfo
  };
};
